import { Injectable } from '@angular/core';
import {
  DlqService
}
from '@gms/monitoring-api';

import {
  FetchDlqMessages,
  FetchDlqMessagesFailure,
  FetchDlqMessagesSuccess,
  DlqActionTypes,
  FetchDlqMessageById,
  FetchDlqMessageByIdSuccess,
  FetchDlqMessageByIdFailure,
  DeleteDlqMessageById,
  DeleteDlqMessageByIdSuccess,
  DeleteDlqMessageByIdFailure,
  RedriveDlqMessage,
  RedriveDlqMessageSuccess,
  RedriveDlqMessageFailure,
  ApproveDlqMessage,
  ApproveDlqMessageSuccess,
  ApproveDlqMessageFailure,
  SubmitNewDlqMessage,
  SubmitNewDlqMessageSuccess,
  SubmitNewDlqMessageFailure,
  SubmitMessageForReview,
  SubmitMessageForReviewSuccess,
  SubmitMessageForReviewFailure,
  DeleteMultipleDlqMessages,
  DeleteMultipleDlqMessagesSuccess,
  DeleteMultipleDlqMessagesFailure
} from 'app/store/dlq/dlq.actions';
import { Observable, of, switchMap } from 'rxjs';
import {
  catchError,
  map,
  } from 'rxjs/operators';
import { sortDefinitions } from '../default-sort.definitions';
import { createEffect, Actions, ofType } from '@ngrx/effects';

@Injectable()
export class DlqEffects {
  constructor(
    private _actions$: Actions,
    private dlqService: DlqService
  ) {}

  FetchDlqMessages: Observable<any> = createEffect(() =>
    this._actions$.pipe(
      ofType<FetchDlqMessages>(DlqActionTypes.FETCH_DLQ_MESSAGES),
      map((action: FetchDlqMessages) => action.payload),
      switchMap(payload => {
        let sortQuery = '';
        if (payload.sortDescriptors) {
          payload.sortDescriptors.forEach(sortDescriptor => {
            sortQuery = `${sortQuery}${sortDescriptor.field}+${sortDescriptor.dir}|`;
          });
        }

        sortQuery = !!sortQuery ? sortQuery : sortDefinitions.getDlqMessages;
        return this.dlqService.getMessages(
          payload.service,
          payload.queueName,
          payload.message,
          payload.archived,
          payload.pendingReview,
          payload.pageSize,
          payload.pageNumber,
          sortQuery)
          .pipe(
            map(
              messageCollection =>
                new FetchDlqMessagesSuccess({
                  messages: messageCollection.messages,
                  totalMessageCount: messageCollection.total
                })
            ),
            catchError(error => of(new FetchDlqMessagesFailure({ error: error })))
          );
      })
    )
  );

  FetchDlqMessageById: Observable<any> = createEffect(() =>
    this._actions$.pipe(
      ofType<FetchDlqMessageById>(DlqActionTypes.FETCH_DLQ_MESSAGE_BY_ID),
      map((action: FetchDlqMessageById) => action.payload),
      switchMap(payload => {
        return this.dlqService.getMessageById(payload.messageId).pipe(
          map(message => new FetchDlqMessageByIdSuccess({ message })),
          catchError(error => of(new FetchDlqMessageByIdFailure({ error })))
        );
      })
    )
  );

  DeleteDlqMessageById: Observable<any> = createEffect(() =>
    this._actions$.pipe(
      ofType<DeleteDlqMessageById>(DlqActionTypes.DELETE_DLQ_MESSAGE_BY_ID),
      map((action: DeleteDlqMessageById) => action.payload),
      switchMap(payload => {
        return this.dlqService.deleteMessageById(payload.messageId).pipe(
          map(() => new DeleteDlqMessageByIdSuccess({ messageId: payload.messageId })),
          catchError(error => of(new DeleteDlqMessageByIdFailure({ error })))
        );
      })
    )
  );

  RedriveDlqMessage: Observable<any> = createEffect(() =>
    this._actions$.pipe(
      ofType<RedriveDlqMessage>(DlqActionTypes.REDRIVE_DLQ_MESSAGE_BY_ID),
      map((action: RedriveDlqMessage) => action.payload),
      switchMap(payload => {
        return this.dlqService.redriveMessageById(payload.messageId).pipe(
          map((updatedMessage) => new RedriveDlqMessageSuccess({ message: updatedMessage })),
          catchError(error => of(new RedriveDlqMessageFailure({ error })))
        );
      })
    )
  );

  ApproveDlqMessage: Observable<any> = createEffect(() =>
    this._actions$.pipe(
      ofType<ApproveDlqMessage>(DlqActionTypes.APPROVE_DLQ_MESSAGE),
      map((action: ApproveDlqMessage) => action.payload),
      switchMap(payload => {
        return this.dlqService.approveMessageById(payload.request, payload.messageId).pipe(
          map(() => new ApproveDlqMessageSuccess({ messageId: payload.messageId })),
          catchError(error => of(new ApproveDlqMessageFailure({ error })))
        );
      })
    )
  )

  SubmitNewMessage: Observable<any> = createEffect(() =>
    this._actions$.pipe(
      ofType<SubmitNewDlqMessage>(DlqActionTypes.SUBMIT_NEW_DLQ_MESSAGE),
      map((action: SubmitNewDlqMessage) => action.payload),
      switchMap(payload => {
        return this.dlqService.submitNewMessageForReview(payload.request).pipe(
          map(() => new SubmitNewDlqMessageSuccess()),
          catchError(error => of(new SubmitNewDlqMessageFailure({ error })))
        );
      })
    )
  )

  SubmitMessageForReview: Observable<any> = createEffect(() =>
    this._actions$.pipe(
      ofType<SubmitMessageForReview>(DlqActionTypes.SUBMIT_MESSAGE_FOR_REVIEW),
      map((action: SubmitMessageForReview) => action.payload),
      switchMap(payload => {
        return this.dlqService.submitMessageForReview(payload.request, payload.messageId).pipe(
          map(() => new SubmitMessageForReviewSuccess()),
          catchError(error => of(new SubmitMessageForReviewFailure({ error })))
        );
      })
    )
  )

  DeleteMultipleDlqMessages: Observable<any> = createEffect(() =>
    this._actions$.pipe(
      ofType<DeleteMultipleDlqMessages>(DlqActionTypes.DELETE_MULTIPLE_DLQ_MESSAGES),
      map((action: DeleteMultipleDlqMessages) => action.payload),
      switchMap(payload => {
        return this.dlqService.deleteMessages(payload.messageIds).pipe(
          map(() => new DeleteMultipleDlqMessagesSuccess({ messageIds: payload.messageIds })),
          catchError(error => of(new DeleteMultipleDlqMessagesFailure({ error })))
        );
      })
    )
  )
}
